import React, { useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface PhotoGalleryProps {
  images: string[];
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ images }) => {
  const [showFullGallery, setShowFullGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowFullGallery(false);
  }, []);

  const nextImage = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === images.length - 1 ? 0 : prev + 1
    );
  }, [images.length]);

  const prevImage = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === 0 ? images.length - 1 : prev - 1
    );
  }, [images.length]);

  return (
    <>
      {/* Grid Gallery */}
      <div className="grid grid-cols-4 gap-2 h-[500px]">
        {/* Main Image */}
        <div 
          className="col-span-2 row-span-2 relative rounded-l-lg overflow-hidden"
          onClick={() => {
            setCurrentImageIndex(0);
            setShowFullGallery(true);
          }}
        >
          <div className="absolute inset-0">
            <img
              src={images[0]}
              alt="Main view"
              className="w-full h-full object-cover"
              style={{ objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Secondary Images */}
        <div className="col-span-2 grid grid-cols-2 grid-rows-2 gap-2">
          {images.slice(1, 5).map((image, index) => (
            <div
              key={index}
              onClick={() => {
                setCurrentImageIndex(index + 1);
                setShowFullGallery(true);
              }}
              className={`relative overflow-hidden ${
                index === 3 ? 'rounded-tr-lg' : index === 1 ? '' : 'rounded-br-lg'
              }`}
            >
              <img
                src={image}
                alt={`View ${index + 2}`}
                className="w-full h-full object-cover"
                style={{ objectFit: 'cover' }}
              />
            </div>
          ))}
        </div>

        {/* Show All Photos Button */}
        {images.length > 5 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowFullGallery(true);
            }}
            className="absolute bottom-4 right-4 bg-white text-gray-900 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors shadow-md"
          >
            Show all {images.length} photos
          </button>
        )}
      </div>

      {/* Full Screen Gallery */}
      {showFullGallery && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onClick={handleClose}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 left-4 z-50 text-white bg-black/50 p-2 rounded-full hover:bg-black/70 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full">
            {currentImageIndex + 1} / {images.length}
          </div>

          {/* Main Image */}
          <div 
            className="relative w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[currentImageIndex]}
              alt={`View ${currentImageIndex + 1}`}
              className="max-h-[90vh] max-w-[90vw] object-contain"
              style={{ objectFit: 'contain' }}
            />
          </div>

          {/* Navigation Buttons */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full hover:bg-white transition-colors"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full hover:bg-white transition-colors"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* Thumbnail Strip */}
          <div 
            className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 overflow-x-auto max-w-[90vw] p-2"
            onClick={(e) => e.stopPropagation()}
          >
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                  index === currentImageIndex ? 'border-white' : 'border-transparent'
                }`}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  style={{ objectFit: 'cover' }}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoGallery;